import React, { useMemo, forwardRef, useState, useImperativeHandle } from "react";
import { Tag, Space, Button } from "@bedrock/components";
import { useControllableValue, useMemoizedFn } from 'ahooks'
import { SFormElement } from "#components/SForm";
import { Download } from "@bedrock/icons-react";
import { $sType } from "#utils";
import { AttachmentBlobDto, outsourceAttachmentDownloadUrl, outsourceDemandGenStatementBlob } from "#services/api";
import "./index.less"

type Props = {
    detail?: any,
    view?: boolean,
    style?: React.CSSProperties,
    handleDownLoad?: (file) => void
}

const Index = (props: Props, ref) => {
    const { detail, view = false, style, handleDownLoad } = props
    const { statement_attachs = [], structured, product_area } = detail
    const [loading, setLoading] = useState(false)
    const [fileList, setFileList] = useControllableValue(props, {
        defaultValue: [],
    })

    const isPOandDomestic = useMemo(() => {
        return structured && product_area === 'DOMESTIC'
    }, [structured, product_area])

    const tipText = useMemo(() => {
        return isPOandDomestic ?
            '注：请生成并下载MPC结算单，进行线下盖章后上传提交' :
            '注：请上传已盖章的结算单附件'
    }, [isPOandDomestic])

    const getState = useMemoizedFn((stateList = []) => {
        return stateList.includes(detail.state)
    })

    const attachmentButton = useMemoizedFn((className = "") => {
        return <Button
            className={className}
            loading={loading}
            onClick={handleDownLoadMPC}
            type="link"
            icon={<Download />}
        >
            生成并下载MPC结算单
        </Button>
    })

    // 判断是否是外包的付款中和已付款节点

    const isPaymentProgressPaid = useMemo(() => {
        const settledUpload = detail?.bottom_tabs?.filter(item => item.value === "settledUpload")
        let flag = true
        // 因为发票待上传和付款中的state是同一个submit_reimburse，所以需要判断detail?.bottom_tabs下面有没有settledUpload
        // 有settledUpload代表就是发票待上传 没有则是付款中
        // 发票待上传节点需要展示
        if (getState(['submit_reimburse']) && settledUpload?.length) {
            flag = true
            // 付款中节点和已付款节点需要隐藏
        } else if (getState(['closed', 'submit_reimburse'])) {
            flag = false
        } else {
            flag = true
        }
        return flag
    }, [detail?.bottom_tabs])

    const renderStatementAttachment = useMemoizedFn(() => {
        if (!view) {
            return null
        }

        if (
            !statement_attachs?.length && // 结算单附件为空
            $sType === 'outer' && // 外包
            detail?.structured && // 结构化
            detail?.product_area === 'DOMESTIC' && // 国内
            !detail?.non_need_fee_control_verify   // 非需要费用控制审核
            && isPaymentProgressPaid // 付款中节点和已付款节点需要隐藏
        ) {
            return attachmentButton()
        }

        return <Space className="statement-attachment-space" size={8}>
            <SFormElement
                view={true}
                className="statement-attachment-upload"
                value={statement_attachs}
                componentClass={'upload'}
            />
            {
                !!statement_attachs.length && <Space size={12} direction="vertical">
                    {statement_attachs?.map(item => {
                        return <Tag color="light-brand" text={'MPC生成'} />
                    })}
                </Space>
            }
        </Space>
    })

    useImperativeHandle(ref, () => ({
        validateFields: () => {
            if (fileList?.some(v => v.status === 'uploading')) {
                return Promise.reject('当前文件正在上传中，请稍后再试')
            }
            if (fileList?.some(v => v.status === 'error')) {
                return Promise.reject('存在上传失败的文件，请删除后再试')
            }
            return Promise.resolve()
        },
    }));

    const renderSignedStatementAttachment = useMemoizedFn(() => {
        const showButton = isPOandDomestic && !view
        if (showButton && getState(['making'])) {
            return attachmentButton()
        }
        return <>
            {showButton && getState(['submit_reimburse']) && attachmentButton('statement-attachment-button')}
            <Space className="statement-attachment-space" size={8}>
                <SFormElement
                    view={view}
                    className="statement-attachment-upload"
                    value={fileList}
                    onChange={setFileList}
                    componentClass={'upload'}
                />
                {
                    view && <Space size={12} direction="vertical">
                        {fileList?.map(item => {
                            return <Tag color="light-green" text={'供应商上传'} />
                        })}
                    </Space>
                }
            </Space>
            {!view && <span className="statement-attachment-upload-tip">{tipText}</span>}
        </>
    })

    const handleDownLoadMPC = () => {
        setLoading(true)
        outsourceDemandGenStatementBlob({ id: detail?.id, attach: true }).then(result => {
            if (result.code === 1) {
                outsourceAttachmentDownloadUrl({ id: result.data.blob_id, is_blob: true, download: 'attachment' }).then(res => {
                    res?.data && window.open(res?.data);
                    typeof handleDownLoad === 'function' && handleDownLoad(result.data)
                }).finally(() => {
                    setLoading(false)
                })
            }
        })
    }

    if (detail?.non_need_fee_control_verify) {
        return null
    }

    return (
        <>
            <Space
                style={{ ...style }}
                className="statement-attachment"
                direction="vertical"
                size={6}
            >
                {renderStatementAttachment()}
                {renderSignedStatementAttachment()}
            </Space>
        </>
    )
}

export default forwardRef(Index);